import { ZCDSelect } from '@zencity/common-ui';
import { SelectOption } from '@zencity/common-ui/lib/ZCD/ZCDFilter/types';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'customHooks/hooks';
import { FieldTitle } from 'screens/WidgetGenerator/components/FieldTitle/FieldTitle';
import { AspectValue } from 'screens/WidgetGenerator/utils/misc';
import { getGenericQuestionsData } from 'screens/WidgetGenerator/widgetAdditionalFields/utilsFunctionsAdditionalFields/GenericQuestionSelection/utils';
import { QuestionType } from 'types/genericQuestion';
import { logger } from 'utils/logger';
import { LoaderMask } from 'components/LoaderMask/LoaderMask';
import { SurveysReportGeneratorFilterKey } from 'screens/WidgetGenerator/reportGeneratorComponents/enums';
import { ReportFilterProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { rakeWeighterExecutionToSelectedDateRange } from 'utils/rakeWeighterExecutions';

interface Props {
  reportBuilderProps: ReportFilterProps<SelectOption>;
  questionTypesToInclude: QuestionType[];
  aspect?: AspectValue;
}

export const GenericQuestionSelectFilter = (props: Props): ReactElement => {
  const { reportBuilderProps, questionTypesToInclude, aspect } = props;
  const { t: translate } = useTranslation();

  const { value: filterValue, onChange, filterValues, clientData } = reportBuilderProps;

  const selectedSurveyGroupId = filterValues?.[SurveysReportGeneratorFilterKey.SURVEY_GROUP];
  const selectedCycleId = filterValues?.[SurveysReportGeneratorFilterKey.CYCLES];

  const handleOnGenericQuestionSelect = (option: unknown) => {
    onChange(option as SelectOption);
  };
  const fetchGenericQuestions = async () => {
    if (!selectedSurveyGroupId || !selectedCycleId) {
      return [];
    }

    const selectedSurveyGroup = clientData.surveysGroups.find(
      (surveyGroup) => surveyGroup.id === selectedSurveyGroupId,
    );
    const selectedCycle = clientData.rakeWeighterExecutionsBySurveyGroup[selectedSurveyGroupId].find(
      (rakeWeighterExecution) => rakeWeighterExecution.id === selectedCycleId,
    );

    if (selectedSurveyGroup === undefined || selectedCycle === undefined) {
      return [];
    }

    const mappedGenericQuestionsOptions = await getGenericQuestionsData({
      aspect,
      selectedDateRange: rakeWeighterExecutionToSelectedDateRange(selectedCycle),
      selectedSurveyGroup,
      questionTypesToInclude,
      zcClientId: clientData.client.zc_client_id,
    });

    return mappedGenericQuestionsOptions;
  };

  const {
    fetchedData: fetchedGenericQuestionsOptions,
    isLoading,
    error,
  } = useAsyncEffect(fetchGenericQuestions, [selectedSurveyGroupId, selectedCycleId]);
  if (error) {
    logger.error(error);
  }

  return (
    <LoaderMask isLoading={isLoading}>
      <FieldTitle title={translate('widgetGenerator.filters.wordCloud.genericQuestionFilterLabel')} />
      <ZCDSelect
        value={filterValue}
        placeholder={translate('common.select')}
        options={fetchedGenericQuestionsOptions}
        isLoading={isLoading}
        onChange={handleOnGenericQuestionSelect}
      />
    </LoaderMask>
  );
};
